import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { auditTime, BehaviorSubject, debounceTime, distinctUntilChanged, map, merge, Observable } from 'rxjs';
import { CommentDialogComponent } from 'src/app/dialogs';
import { TopicType } from 'src/app/enums/topic-type.enum';
import { CommentConfig } from 'src/app/models/comment-config.model';
import { CommentDialogData } from 'src/app/models/dialog/comment-dialog-data.model';
import { IResultModel, Result, ResultModel } from 'src/app/models/general/result.model';
import { Kpi, KpiModel } from 'src/app/models/kpi.model';
import { KpiType } from 'src/app/models/kpiType.model';
import { ReviewPeriod } from 'src/app/models/review-period.model';
import { State } from 'src/app/models/state.model';
import { StateService } from 'src/app/services';
import { KpiService } from 'src/app/services/kpi.service';
import { SearchComponent } from 'src/app/shared/components';
import { CustomValidators, DateHelper } from 'src/app/shared/helpers';
import { ActionStateModel } from 'src/app/shared/models/action-state.model';
import { ListItem } from 'src/app/shared/models/list-item.model';
import { SearchConfig } from 'src/app/shared/models/search-config.model';
import { SearchOption } from 'src/app/shared/models/search-option.model';
import { ErrorMessageService } from 'src/app/shared/services';
import { Comment } from 'src/app/models/comment.model';
import { GoalPeriod } from 'src/app/models/goalperiod.model';
import { GoalPermissionsHelper } from 'src/app/helpers/goal-permissions.helper';
import { Search } from 'src/app/models/search.model';
import { KpiCalculationType } from 'src/app/enums/kpi-calculation-type.enum';
import { GoalsHelper } from 'src/app/helpers/goals.helper';

@UntilDestroy()
@Component({
  selector: 'app-goal-kpi-edit',
  templateUrl: './goal-kpi-edit.component.html',
  styleUrls: ['./goal-kpi-edit.component.scss']
})
export class GoalKpiEditComponent implements OnInit {

public months: { id: number, name: string }[] = [];
   // Inputs

    @Input() public currentPeriod: GoalPeriod; //ReviewPeriodGoalsForEmployee;
    @Input() public aboutEmployeeId: number;
    @Input() public kpi: Kpi;
    @Input() public readonly = false;
    
    // Outputs

    @Output() public added = new EventEmitter<Kpi>();
    @Output() public updated = new EventEmitter<Kpi>();
    @Output() public deleted = new EventEmitter<number>();

    showGauge = true;
    gaugeType = "full";
    gaugeAppendText = "%";
    gaugeSize = 80;

    isGaugeMedium = false;
    isDialogOpened = false;

    originalFormData: any;

    public actionState: ActionStateModel = {editMode: this.readonly ? false : true, currentItemIndex: null} as ActionStateModel;
    public itemsSubject = new BehaviorSubject<ListItem<Kpi>[]>([]);
    public readonly items$ = this.itemsSubject.asObservable();
    public reviewPeriod$: Observable<ReviewPeriod>;

    private readonly fieldsToWatch: string[] = ['name', 'planned','weight', 'deadline', 'minExpectation', 'calculationType'];

    public kpiForm: FormGroup;
    public state: State;

    public completionLogicOptions = [
      { value: KpiCalculationType.GreaterOrEqual, text: 'az elért eredmény nagyobb vagy egyenlő, mint a kitűzött eredmény', tooltip: 'az elért eredmény nagyobb vagy egyenlő, mint a kitűzött eredmény (pl. árbevétel, új ügyfélszám – minél több, annál jobb)' },
      { value: KpiCalculationType.LessOrEqual, text: 'az elért eredmény kisebb vagy egyenlő, mint a kitűzött eredmény', tooltip: 'az elért eredmény kisebb vagy egyenlő, mint a kitűzött eredmény (pl. fluktuáció, hibaszám – minél kevesebb, annál jobb)' },
      { value: KpiCalculationType.ExactlyEqual, text: 'az elért eredmény pontosan megegyezik a kitűzött eredménnyel', tooltip: 'az elért eredmény pontosan megegyezik a kitűzött eredménnyel (pl. létszám, költségkeret – csak akkor jó, ha pont ugyanannyi)' }
    ];

    public get isExpectationNotMet(): boolean {
      const planned = this.kpiForm.get('planned').value;
      const achieved = this.kpiForm.get('achieved').value;
      const calculationType = this.kpiForm.get('calculationType').value as KpiCalculationType;
      const minExp = this.kpiForm.get('minExpectation').value;

      if (minExp == null) return false;

      const maxValue = this.currentPeriod?.reviewPeriod?.reviewPeriodGoalSettings?.numericGoalMaxResult;
      const ratio = GoalsHelper.calculateKPIRatio(planned, achieved, calculationType, maxValue);
      return ratio < minExp;
    }

    public get gaugeValue(): number {
      const planned = this.kpiForm.get('planned').value;
      const achieved = this.kpiForm.get('achieved').value;
      const calculationType = this.kpiForm.get('calculationType').value as KpiCalculationType;

      if (planned == null || achieved == null)
        return 0;
      
      const maxValue = this.currentPeriod?.reviewPeriod?.reviewPeriodGoalSettings?.numericGoalMaxResult;
      const result = Math.round(GoalsHelper.calculateKPIRatio(planned, achieved, calculationType, maxValue));

      if (!maxValue) return result;

      return result > maxValue ? maxValue : result;
    }

    public get gaugeLabel(): string {
      return '';
    }

    public get getOwnComment(): Comment {
      return this.kpi?.comments.find(c => c.employeeId === this.state.me.value.employee.id) ?? null;
    }

    public get hasPlannedLessThanMinError(): boolean {
      return !!(this.kpiForm.errors?.['plannedLessThanMinExpectation']);
    }

    public get plannedLessThanMinErrorMessage(): string {
      return this.msgService.getErrorText('plannedLessThanMinExpectation');
    }
              
    // public get allowEdit(): boolean {
    //   return (this.stateService.state.isSuperAdmin || this.stateService.state.isAdmin) && this.currentPeriod.isBossOfSelectedEmployee;
    // }
    
    public kpiSearchConfig: SearchConfig<KpiType, string> = {
      mapItem: (model: KpiType) => {
        return {
          id: model.name, 
          text: model.name,
          dropText: model.name + ' (' + model.unit + ')',
          itemSource: model } as SearchOption<KpiType, string>;
      },
      getList: this.kpiService.getTypeList,
    };
  
    @ViewChild('search')
    search: SearchComponent<KpiType, string>;
  
    // public get allowAdd(): boolean {
    //   return this.itemsSubject.getValue().length === 0 || this.itemsSubject.getValue().every(x => x.state === ComponentState.View);
    // }
  
    constructor(
      public dialog: MatDialog,
      private fb: UntypedFormBuilder,
      private kpiService: KpiService,
      private msgService: ErrorMessageService,
      public breakpointObserver: BreakpointObserver,
      private stateService: StateService,
      private translate: TranslateService) {
        this.state = stateService.state;
    }
  
    ngOnInit(): void {
      this.months = DateHelper.getMonths();
      this.kpiForm = this.createForm();

       this.kpiSearchConfig.options =  {employeeId: this.aboutEmployeeId} as Search;

      //this.loadList();
      this.resizeGauge();
      this.loadData();
      this.setupFieldChangeSubscriptions();
    }

    public loadData() {

      if (this.kpi != null)
        this.editItem(this.kpi);
    }

    public createCommentConfig(item: Kpi): CommentConfig {
        return {
            employeeId: this.state?.me.value?.employee?.id ?? 0,
            topicId: TopicType.Kpi,
            topicRef: item.id,     // assessmentId
            author: this.state?.me.value?.employee?.name,
        } as CommentConfig;
    }

    public allowComment(): boolean {
      return GoalPermissionsHelper.canComment(this.currentPeriod, this.state);
    }

    public allowDelete(): boolean {
      return GoalPermissionsHelper.canDeleteKPI(this.currentPeriod, this.state);
    }

    // Alap mezők szerkesztés jogosultsága, mint megnevezés, egység stb.
    public isAllowEditBasicFields(): boolean {
      return GoalPermissionsHelper.canEditBasicFields(this.currentPeriod, this.state);
    }

    // Speciális vezérlő mezők szerkesztés jogosultsága, mint pl. minimum elvárt eredmény, súlyozás, stb.
    public isAllowEditSpecFields(): boolean {
      return GoalPermissionsHelper.canEditKPISpecFields(this.currentPeriod, this.state);
    } 

    // Értékelő mezők szerkesztés jogosultsága
    public isAllowEditAssessmentFields(): boolean {
      return GoalPermissionsHelper.canEditGoalRatings(this.currentPeriod, this.state);
    }

    public editItem(item: Kpi) {      
      const formData = this.createFormData(item);
      this.originalFormData = {...formData};

      this.kpiForm.patchValue(formData);
      this.setFormState(item);
      this.setValidators();
    }

    public setValidators() {
      //set [Validators.min(1), Validators.max()] for 'minExpectation'
      const minExpectationControl = this.kpiForm.get('minExpectation');
     
      const maxValue = this.currentPeriod?.reviewPeriod?.reviewPeriodGoalSettings?.numericGoalMaxResult;
      if (maxValue) {
        minExpectationControl.setValidators([Validators.min(0), Validators.max(maxValue)]);
      } else {
        minExpectationControl.setValidators([Validators.min(0)]);
      }

      this.kpiForm.updateValueAndValidity();
    }

    public setFormState(item: Kpi) {
      if (!this.isAllowEditBasicFields())
      {
        //disable all controls except 'achieved'
        this.kpiForm.get('name').disable();
        this.kpiForm.get('calculationType').disable();
        this.kpiForm.get('planned').disable();
        this.kpiForm.get('unit').disable();
      }

      if (!this.isAllowEditSpecFields())
      {
        this.kpiForm.get('minExpectation').disable();
      }

      if (!this.isAllowEditAssessmentFields())
      {
        this.kpiForm.get('achieved').disable();
      }

      if (!GoalPermissionsHelper.canEditWeight(this.currentPeriod, this.state)) {
        this.kpiForm.get('weight').disable();
      }

      if (!GoalPermissionsHelper.canEditDeadline(this.currentPeriod, this.state)) {
        this.kpiForm.get('deadline').disable();
      }

      let nameControl = this.kpiForm.get('name');

      if (nameControl != null && !nameControl.value) {

        nameControl.setValue(this.kpiSearchConfig.mapItem({name: item.name, unit: item.unit} as KpiType), {emitEvent: false});
      }
    }

    private createForm(): FormGroup {
      return this.fb.group({
        'id': 0,
        'isAssessed': false,
        'noAnswer': false,
        'error': false,
        'name': ['', [Validators.required]],
        'unit': ['', [Validators.required]],
        'isCommon': false,

        'aboutUserId': '',
        'aboutEmployeeId': null,
        'reviewPeriodId': null,

        'planned': ['', [Validators.min(0)]],
        'achieved': [''],
        'deadline': null,
        'weight': [null, [
            Validators.min(1),
            Validators.max(100),
            Validators.pattern(/^\d+$/)
          ]],
        'minExpectation': [null],
        'calculationType': [KpiCalculationType.GreaterOrEqual, [Validators.required]]
      }, {
            validators: [
               
            ]
      });
    }

     private createFormData(result: Kpi): any {

        let kpiName: SearchOption<KpiType, string> = null;

        kpiName = this.kpiSearchConfig.mapItem({name: result.name, unit: result.unit});

      const formData = {
        id: result.id,
        planned: result.planned,
        achieved: result.achieved,
        aboutEmployeeId: result.aboutEmployeeId,
        aboutUserId: result.aboutUserId,
        reviewPeriodId: result.reviewPeriodId,
        isAssessed: result.isAssessed,
        noAnswer: result.noAnswer,
        error: result.error,
        name: result.name,
        unit: result.unit,
        deadline: result.deadline,
        weight: result.weight,
        minExpectation: result.minExpectation,
        calculationType: result.calculationType ?? KpiCalculationType.GreaterOrEqual
      } as Kpi;

      return {...formData, name: kpiName};
    }

    public onBlur($event) {
      const formData = this.kpiForm.getRawValue();
      
      if (!this.kpiForm.dirty || this.kpiForm.invalid || this.isDialogOpened) return;
    }

    public saveItem(model: Kpi) {
      if (!this.kpiForm.dirty || this.kpiForm.invalid) return;

      const formData = this.kpiForm.getRawValue();

      let action: Observable<Result<KpiModel>>;

      //model.planned = Number(formData.planned);
      //model.achieved = Number(formData.achieved) ? Number(formData.achieved) : null;
      //model.kpiType = formData.kpiType.itemSource;

      if (model.id)
      {
        action = this.kpiService.updateData(model);
      }
      else
      {
        action = this.kpiService.addData(model);
      }

      action.subscribe({ next: (result) => {
        //this.card.displaySavedStatus();
        const res = result.data;

        this.kpi.id = res.id;
        this.kpi.name = res.name;
        this.kpi.unit = res.unit;
        this.kpi.achieved = res.achieved;
        this.kpi.planned = res.planned;
        this.kpi.calculationType = res.calculationType;

        this.kpiForm.get('id').setValue(res.id, {emitEvent: false});

        this.kpiForm.markAsPristine();

        this.originalFormData = {...this.kpiForm.getRawValue()};

        //this.search.disabled = false;

      }, error:(err: IResultModel<ResultModel>) => {
        this.msgService.handleError<ResultModel>(err);
      }});
    }
  
    // showConfirmDialog(model: Kpi, key: string) {
    //   this.isDialogOpened = true;
    //   const msg = this.msgService.getErrorMessage(key);

    //   const dialogRef = this.dialog.open(ConfirmDialogComponent, {
    //     maxWidth: "500px",
    //     data: {
    //         title: 'Kérem válasszon a folytatáshoz',
    //         message: msg.text,
    //         choices: [
    //           {key: 'updateCurrent', name: 'Csak ebben az esetben', color: 'primary'},
    //           {key: 'updateAll', name: 'Minden esetben', color: 'primary'},
    //         ]
    //     } as IConfirmDialogModel
    //   });

    //   dialogRef.afterClosed().subscribe( (dialogResult: string) => {
    //     this.isDialogOpened = false;
    //     if (dialogResult)
    //     {
    //       if (dialogResult == 'updateCurrent')
    //         model.updateType = UpdateType.UpdateCurrent
    //       else if (dialogResult == 'updateAll')
    //         model.updateType = UpdateType.UpdateAll;

    //       this.saveItem(model);
    //     }
    //     else
    //     {
    //       //this.card.clearStatus();
    //       this.kpiForm.reset(this.originalFormData);
    //     }
    //  });
    // }

    changeUnit($event)
    {
      //if (this.kpiForm.get('unit').value != newFormValue.unit)
      this.kpiForm.get('unit').setValue($event.target.value);
    }

    public resizeGauge() {
      // const width = window.innerWidth;
            
      // if (width < 1060)      
      //   this.gaugeSize = 160;
      // else if (width < 1200)
      //   this.gaugeSize = 160;
      // else 
      //   this.gaugeSize = 82;

       this.breakpointObserver
        .observe(['(max-width: 1100px)'])
        .pipe(
          map (state => {
            if (state.breakpoints['(max-width: 1100px)']) return 160;
            return 82
          }),
          distinctUntilChanged(),
          untilDestroyed(this)
        )
        .subscribe(size=> {
          if (this.gaugeSize !== size)
            this.gaugeSize = size;
        });
    }
       
    public deleteKPI() {
       if (confirm(this.translate.instant("GENERAL.DELETE_CONFIRM"))) {
        this.deleted.emit();
      }
    }
   
    onSearchChange($event) {
      if ($event?.itemSource)
      {
        const item = $event?.itemSource;

        //this.kpiForm.get('name').setValue(item.name, {emitEvent: false});
        this.kpiForm.get('unit').setValue(item.unit, {emitEvent: false});
      }         
    }

    public getInputSize(field: string): string {
      let size = 18;
      const value = this.kpiForm.get(field).value;
            
      if (value != null) {
        length = value.toString().length;

        if (length > 7)
        {          
          if (length < 10)
            size = 22;
          else if (length < 13)
            size = 16;
          else
            size = 14;
        }
      }

      return size + 'px';
    }

    // calculates the font size based on the length of the value to fit within the maxWidth
    public getFontSize(value: string, maxWidth: number, maxSize: number, minSize: number): string {
      let size = 18;
      let length = value.toString().length;
      
      if (length > 0) {
        size = Math.max(Math.min(maxWidth / length, maxSize), minSize);
      }
      if (size < 12) size = 12; // minimum font size
      if (size > maxSize) size = maxSize; // maximum font size
      
      return size + 'px';
    }

    public showCommentDialog()
    {
        if (!this.kpi) 
          return;
    
        let dialogData: CommentDialogData = {
          topicId: TopicType.Kpi, 
          topicRef: this.kpi.id,
          noSave: true,
          comment: this.getOwnComment ? {...this.getOwnComment } : null,
        };
    
        const dialogRef = this.dialog.open(CommentDialogComponent, {
          minWidth: "500px",
          data: dialogData,
        });
    
        dialogRef.afterClosed().subscribe( (dialogResult: Comment) => {
          if (dialogResult)
          {         
            let comment = dialogResult.message ? {... dialogResult, author: this.state?.me.value?.employee.name} : dialogResult;
            
            this.manageCommentDialogResult(comment, this.kpi);
          }
          
        });
    }

    // kezeli a komment párbeszédablak eredményét
    // ha a komment.message üres, az a komment törölhető lesz
    public manageCommentDialogResult(comment: Comment, entity: Kpi) {
        const index = entity.comments.findIndex(c => c.employeeId === comment.employeeId);

        if (comment?.message) {

          if (index >= 0) {
            entity.comments[index] = comment;   // update
          } else {
            entity.comments.push(comment);  // add
          }
        }
        else
        {
          if (!comment.id)
            entity.comments = entity.comments.filter(c => c.employeeId !== this.getOwnComment.employeeId);
          else
          {
            entity.comments[index] = comment;   // update, ami törlést fog okozni a backenden
          }
        }
    }

    private onSpecFieldsChanged(): void {
      this.kpiForm.get('achieved').setValue(null);
    }

    private setupFieldChangeSubscriptions(): void {
      const changes = this.fieldsToWatch
        .map(field => this.kpiForm.get(field)?.valueChanges)
        .filter(obs => obs !== undefined);
  
      if (changes.length > 0) {
        merge(...changes)
          .pipe(
            debounceTime(300), // Vár 300ms-t, hogy a felhasználó "végezzen" a mezővel
            untilDestroyed(this)
          )
          .subscribe(() => this.onSpecFieldsChanged());
      }
    }

}
